'use client'
import { useRef, useEffect } from 'react'

/* Lightweight rich-text editor — no editor library, just a contentEditable with a FIXED toolbar.
   Toolbar is EXACTLY: Bold, Underline, Highlight, Text colour. Nothing else — no images, no
   embeds, no links, and paste is forced to PLAIN TEXT so no arbitrary HTML gets through the UI.
   It emits simple markup (<b>/<u>/<mark>/<span style="color">); the server RE-sanitizes every
   write against the same allow-list (src/lib/sanitize-richtext.ts) — that is the real boundary.

   Existing plain-text values load fine (assigned straight into innerHTML on mount). */
export default function RichTextEditor({
  value, onChange, placeholder, ariaLabel,
}: {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  ariaLabel?: string
}) {
  const ref = useRef<HTMLDivElement>(null)

  // Seed the initial HTML once on mount; afterwards the DOM is the source of truth (a controlled
  // contentEditable fights the caret). Parent resets (e.g. form clear) are handled by `key`.
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) ref.current.innerHTML = value || ''
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const emit = () => { if (ref.current) onChange(ref.current.innerHTML) }

  const exec = (command: string, arg?: string) => {
    // styleWithCSS makes foreColor/hiliteColor emit <span style> instead of legacy <font>.
    try { document.execCommand('styleWithCSS', false, 'true') } catch {}
    try { document.execCommand(command, false, arg) } catch {}
    ref.current?.focus()
    emit()
  }

  const onPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    try { document.execCommand('insertText', false, text) } catch {}
    emit()
  }

  return (
    <div className="rte">
      <div className="rte-tb" role="toolbar" aria-label="Text formatting">
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')} aria-label="Bold" title="Bold"><b>B</b></button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('underline')} aria-label="Underline" title="Underline"><u>U</u></button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('hiliteColor', '#fde68a')} aria-label="Highlight" title="Highlight"><mark>H</mark></button>
        <label className="rte-color" title="Text colour">
          <span aria-hidden>A</span>
          <input type="color" aria-label="Text colour" defaultValue="#0b132b" onChange={(e) => exec('foreColor', e.target.value)} />
        </label>
      </div>
      <div
        ref={ref}
        className="rte-area"
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel || placeholder || 'Rich text'}
        data-placeholder={placeholder || ''}
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        suppressContentEditableWarning
      />
      <style jsx>{`
        .rte{border:1px solid #d9d6cc;border-radius:10px;overflow:hidden;background:#fff}
        .rte-tb{display:flex;gap:2px;padding:6px;border-bottom:1px solid #eceae2;background:#faf9f5}
        .rte-tb button,.rte-color{display:inline-flex;align-items:center;justify-content:center;min-width:30px;height:30px;border:1px solid transparent;border-radius:7px;background:transparent;cursor:pointer;font-size:14px;color:#2a2a2a;padding:0 8px}
        .rte-tb button:hover,.rte-color:hover{background:#efece2;border-color:#e0ddd2}
        .rte-color{position:relative;gap:4px}
        .rte-color span{font-weight:700}
        .rte-color input{position:absolute;inset:0;opacity:0;cursor:pointer;width:100%;height:100%}
        .rte-area{min-height:120px;padding:12px 14px;font-size:14px;line-height:1.6;color:#1a1a1a;outline:none;overflow-wrap:anywhere}
        .rte-area:empty:before{content:attr(data-placeholder);color:#9a978c}
        .rte-area :global(mark){background:#fde68a;padding:0 1px}
      `}</style>
    </div>
  )
}
